import type { RequestOptions } from '@@/plugin-request/request';
import { history, type RequestConfig } from '@umijs/max';
import { notification } from 'antd';

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}

// 与后端约定的响应数据格式
interface ResponseStructure {
  success: boolean;
  data: any;
  errorCode?: number;
  errorMessage?: string;
  showType?: ErrorShowType;
}

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      const { success, data, errorCode, errorMessage, showType } =
        res as unknown as ResponseStructure;
      if (!success) {
        const error: any = new Error(errorMessage);
        error.name = 'BizError';
        error.info = { errorCode, errorMessage, showType, data };
        throw error; // 抛出自制的错误
      }
    },
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;

      // 我们的 errorThrower 抛出的错误。
      if (error.name === 'BizError') {
        const errorInfo: ResponseStructure | undefined = error.info;
        if (errorInfo) {
          const { errorMessage, errorCode } = errorInfo;
          switch (errorInfo.showType) {
            case ErrorShowType.SILENT:
              // do nothing
              break;
            case ErrorShowType.WARN_MESSAGE:
              notification.open({
                description: errorMessage,
                title: errorCode,
              });
              break;
            case ErrorShowType.ERROR_MESSAGE:
              notification.open({
                description: errorMessage,
                title: errorCode,
              });
              break;
            case ErrorShowType.NOTIFICATION:
              notification.open({
                description: errorMessage,
                title: errorCode,
              });
              break;
            case ErrorShowType.REDIRECT:
              // TODO: redirect
              break;
            default:
              notification.open({
                description: errorMessage,
                title: errorCode,
              });
          }
        }
      } else if (error.response) {
        // Axios 的错误
        // 请求成功发出且服务器也响应了状态码，但状态代码超出了 2xx 的范围
        notification.warning({
          description: `网络请求遇到问题，状态码：${error.response.status}。请检查网络连接或稍后重试。`,
          title: '请求失败',
        });
      } else if (error.request) {
        // 请求已经成功发起，但没有收到响应
        // \`error.request\` 在浏览器中是 XMLHttpRequest 的实例，
        // 而在node.js中是 http.ClientRequest 的实例
        notification.info({
          description: '网络似乎有点慢，请检查您的连接并稍后重试',
          title: '请求超时',
        });
      } else {
        // 发送请求时出了点问题 - 错误主要显示
        notification.error({
          description: `${error.message}`,
          title: '提示',
        });
      }

      // 重新抛出错误，确保业务代码的 catch 能接收到
      throw error;
    },
  },

  // 请求拦截器
  requestInterceptors: [
    (config: RequestOptions) => {
      const url = config?.url || '';
      const token = localStorage.getItem('token') || '';
      return { ...config, url, headers: { ...config.headers, token } };
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    (response) => {
      const { data } = response as unknown as ResponseStructure;

      // 成功响应
      if (data?.code === 0) {
        return response;
      }

      // 登录失效 - 清除token并跳转登录页
      if (data?.code === 1001) {
        localStorage.removeItem('token');
        notification.warning({
          description: '您的登录已过期，为了您的账户安全，请重新登录。',
          title: '登录过期',
        });
        history.replace({
          pathname: '/user/login',
        });
        // 返回response避免后续处理，但业务层需要处理这种情况
        return response;
      }

      // 其他错误 (code === 9999 或其他非0值) - 抛出错误由errorHandler统一处理
      if (data?.code !== undefined && data?.code !== 0) {
        throw new Error(data?.message || '请求失败，请稍后再试');
      }

      return response;
    },
  ],
};
